import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Filter as FilterIcon } from 'lucide-react';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import FilterDrawer from '../../../shared/FilterDrawer';
import ColumnPicker from '../../../shared/ColumnPicker';
import useTabColumns from '../../../shared/useTabColumns';
import CancelButton from '../../../shared/CancelButton';
import { CategoryPill } from '../../../shared/Pill';
import { Pagination, PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { formatPrice } from '../Prices/priceLookupShared';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { formatDate } from '../../../shared/tenantClock';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { PaymentStatusPill, balanceText } from '../shared/DocumentPayments';
import OriginFilterFields, { ORIGIN_OPTIONS, useDocumentCreators } from '../shared/OriginFilterFields';
import { askConfirm } from '../../../shared/AppDialogs';

const STATUS_LABELS = {
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Accepted',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  expired: 'Expired',
};

// Pinned rather than left to CategoryPill's default hash — see
// QuotePage.js's own copy of this map for why (Withdrawn/Converted
// otherwise land on the same accidental green).
const QUOTE_STATUS_COLORS = {
  draft: '#7b1fa2',
  sent: '#1976d2',
  accepted: '#455a64',
  rejected: '#5d4037',
  withdrawn: '#c2185b',
  expired: '#ef6c00',
};
const CONVERTED_COLOR = '#2e7d32';

const FILTER_SCREEN_KEY = 'quotes';
const DEFAULT_FILTERS = { status: 'all', origin: '', created_by: '' };
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };
// Optional list-table columns (Columns picker, persisted per-user via
// /api/column-preferences) — Date/Quote #/Customer/Amount/actions stay
// always-visible (SYSTEM_COLUMNS) since they're identity columns, not
// display preference. Few enough to all start on and still fit a laptop
// width (ui-architecture.md Column Picker).
const OPTIONAL_COLUMNS = [
  { key: 'po_reference', label: 'PO Reference' },
  { key: 'status', label: 'Status' },
  // Unpaid / Part-paid / Paid / No charge for a Prepay Customer's Quote (issue #338).
  { key: 'payment', label: 'Payment' },
  { key: 'origin', label: 'Origin' },
  { key: 'created_by', label: 'Created by' },
];
const SYSTEM_COLUMNS = ['Date', 'Quote #', 'Customer', 'Amount', 'Actions'];
const DEFAULT_VISIBLE_COLUMNS = ['po_reference', 'status', 'payment'];
// A Quote is only ever made by staff or (later) from the portal.
const QUOTE_ORIGINS = ORIGIN_OPTIONS.filter((o) => o.value !== 'converted');

// Quotes list — only Draft is reachable yet (see the Quotes vision), so
// every row here is a Draft in progress until Send/lifecycle stories land.
function QuotesTable() {
  const { canCreate } = useScreenAccess();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('quote_date');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  // A dashboard tile can open this list already filtered (initialFilters).
  const location = useLocation();
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS, ...(location.state?.initialFilters || {}) }));
  const { status, origin, created_by: createdBy } = filters;
  const creators = useDocumentCreators('/api/quotes/creators');
  // Free-text, unbounded — Quote #, Customer or PO reference (the backend's
  // `search`), deliberately an ad hoc search box rather than a
  // SavedFilterTabs dimension (same split Pricelists/Products use for Name
  // search — see PricelistTable.js).
  const [searchText, setSearchText] = useState('');
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const tabColumns = useTabColumns(OPTIONAL_COLUMNS, SYSTEM_COLUMNS);
  const showPoReference = visibleColumns.includes('po_reference');
  const showStatus = visibleColumns.includes('status');
  const showPayment = visibleColumns.includes('payment');
  const showOrigin = visibleColumns.includes('origin');
  const showCreatedBy = visibleColumns.includes('created_by');
  const totalCols = 5 // Date, Quote #, Customer, Amount, actions
    + OPTIONAL_COLUMNS.filter((c) => visibleColumns.includes(c.key)).length;
  const applyFilters = (values) => { setFilters(values); setPage(1); };
  // Ad hoc "Filters" panel — draft is null while closed, so opening always
  // starts from the currently-applied filters rather than whatever was left
  // over from the last time it was open.
  const [filterDraft, setFilterDraft] = useState(null);
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };

  useDefaultPageSize(setLimit);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/quotes', { params: { status, origin: origin || undefined, created_by: createdBy || undefined, search: searchText || undefined, sortBy, sortOrder, page, limit } });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, origin, createdBy, searchText, sortBy, sortOrder, page, limit]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(col);
      setSortOrder('ASC');
    }
  };

  const handleDelete = async (item) => {
    if (!(await askConfirm(`Delete Draft Quote "${item.display_number}"?`, { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/quotes/${item.id}`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Delete failed');
    }
  };

  const handleConvert = async (item) => {
    if (!(await askConfirm(`Convert ${item.display_number} to a Sales Order?`, { confirmLabel: 'Convert' }))) return;
    try {
      const res = await axios.post(`/api/quotes/${item.id}/convert-to-sales-order`);
      dismissError();
      navigate(`/sales-orders/${res.data.data.id}`, { state: { justConverted: true } });
    } catch (err) {
      showError(err, 'Failed to convert Quote to Sales Order');
    }
  };

  const renderDrawerFields = (draft, setDraftField) => (
    <div>
      <label style={FIELD_LABEL_STYLE}>Status</label><br />
      <select className="styled-input" value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_INPUT_STYLE}>
        <option value="all">All statuses</option>
        {Object.entries(STATUS_LABELS).map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
      <OriginFilterFields draft={draft} setDraftField={setDraftField} creators={creators} origins={QUOTE_ORIGINS} />
    </div>
  );

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Quotes</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              onClick={() => navigate('/quotes/new')}
              style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: 'pointer' }}
            >
              New Quote
            </button>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <input
            className="styled-input"
            placeholder="Quote #, Customer or PO ref..."
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            style={{ width: 230 }}
          />
          {searchText && (
            <button type="button" onClick={() => { setSearchText(''); setPage(1); }} title="Clear search" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
          )}
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: activeFilterCount ? '#eef1f5' : '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
        <ColumnPicker
          screenKey={FILTER_SCREEN_KEY}
          columns={OPTIONAL_COLUMNS}
          defaultVisible={DEFAULT_VISIBLE_COLUMNS}
          onChange={setVisibleColumns}
          tabColumns={tabColumns}
          systemColumns={SYSTEM_COLUMNS}
        />
      </div>

      <div style={{ margin: '10px 24px 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          tabColumns={tabColumns}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the quote list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      <div style={{ margin: '18px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '12%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: showPoReference ? '27%' : '35%' }} />
            {showPoReference && <col style={{ width: '13%' }} />}
            {showStatus && <col style={{ width: '12%' }} />}
            {showPayment && <col style={{ width: '10%' }} />}
            {showOrigin && <col style={{ width: '10%' }} />}
            {showCreatedBy && <col style={{ width: '14%' }} />}
            <col style={{ width: '15%' }} />
            <col style={{ width: '9%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} onClick={() => handleSort('quote_date')}>
                Date {sortBy === 'quote_date' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
              </th>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} onClick={() => handleSort('number')}>
                Quote # {sortBy === 'number' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
              </th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Customer</th>
              {showPoReference && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>PO Reference</th>}
              {showStatus && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>}
              {showPayment && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Payment</th>}
              {showOrigin && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Origin</th>}
              {showCreatedBy && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Created by</th>}
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'right' }}>Amount</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <tr
                key={item.id}
                className="list-row"
                onClick={() => navigate(`/quotes/${item.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <td style={{ padding: '6px 8px', fontSize: 13, color: '#666' }}>{formatDate(item.quote_date)}</td>
                <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{item.display_number}</td>
                <td style={{ padding: '6px 8px', fontSize: 14 }}>{item.customer?.name}</td>
                {showPoReference && <td style={{ padding: '6px 8px', fontSize: 13, color: '#666' }}>{item.po_reference || '—'}</td>}
                {showStatus && (
                  <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                    <CategoryPill
                      value={item.converted_sales_order ? 'Converted' : (STATUS_LABELS[item.status] || item.status)}
                      color={item.converted_sales_order ? CONVERTED_COLOR : QUOTE_STATUS_COLORS[item.status]}
                    />
                  </td>
                )}
                {showPayment && <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}><PaymentStatusPill status={item.payment_status} title={item.payment_status ? balanceText(item.balance_owing) : undefined} /></td>}
                {showOrigin && <td style={{ padding: '6px 8px', fontSize: 13, color: '#666' }}>{item.origin_label || '—'}</td>}
                {showCreatedBy && <td style={{ padding: '6px 8px', fontSize: 13, color: '#666' }}>{item.created_by_name || '—'}</td>}
                <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 14 }}>{formatPrice(item.total_amount)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'copy', needs: 'create', label: 'Copy', onClick: () => navigate('/quotes/new', { state: { copyFromId: item.id } }) },
                      {
                        key: 'convert',
                        label: 'Convert to SO',
                        disabled: item.status !== 'accepted' || !!item.converted_sales_order,
                        disabledTitle: item.status !== 'accepted'
                          ? 'Only an Accepted Quote can be converted'
                          : (item.converted_sales_order ? 'Already converted to a Sales Order' : undefined),
                        onClick: () => handleConvert(item),
                      },
                      {
                        key: 'delete',
                        label: 'Delete',
                        danger: true,
                        disabled: item.status !== 'draft',
                        disabledTitle: item.status !== 'draft' ? 'Only a Draft Quote can be deleted' : undefined,
                        onClick: () => handleDelete(item),
                      },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Quotes"
        rightContent={(
          <select className="styled-input" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}>
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default QuotesTable;
